import type { QueryClient } from '@tanstack/react-query';
import type { ServerEvent, ServerEventType } from '@email/shared';
import { toast } from 'sonner';
import { useSyncStore } from '../stores/sync.js';
import { invalidateServerEvent } from './query-cache.js';

const serverEventTypes = {
  'preferences.updated': true,
  'sender_images.updated': true,
  'mail_rules.updated': true,
  'mail_templates.updated': true,
  'mail_recovery.updated': true,
  'mail_snooze.updated': true,
  'automation.updated': true,
  'automation.activity': true,
  'ai.updated': true,
  'ai.activity': true,
  'calendar.updated': true,
  'contacts.updated': true,
  'planner.updated': true,
  'notifications.updated': true,
  'draft.updated': true,
  'account.updated': true,
  'folder.counts': true,
  'sync.status': true,
  'sync.progress': true,
  'mail.updated': true,
  'message.new': true,
  'thread.updated': true,
  'message.flags': true,
  'message.body_ready': true,
  'op.failed': true,
  'op.updated': true,
  'outbox.updated': true,
} satisfies Record<ServerEventType, true>;

/**
 * Single EventSource for the app. Named events map to query invalidations;
 * only ACTIVE queries refetch (TanStack default), so background lists just
 * go stale and refetch instantly on visit.
 */
export function startSse(qc: QueryClient): () => void {
  const es = new EventSource('/api/stream');
  let connectedOnce = false;

  es.addEventListener('open', () => {
    if (connectedOnce) {
      // The in-process event bus does not replay a disconnect gap. Refresh
      // active screens after EventSource reconnects so timed work and any
      // other missed mutation cannot leave a browser cache stranded.
      void qc.invalidateQueries();
    }
    connectedOnce = true;
  });

  const on = <T extends ServerEventType>(
    type: T,
    handler: (event: Extract<ServerEvent, { type: T }>) => void,
  ) => {
    es.addEventListener(type, (raw) => {
      try {
        handler(JSON.parse((raw as MessageEvent).data));
      } catch {
        /* malformed event — ignore */
      }
    });
  };

  for (const type of Object.keys(serverEventTypes) as ServerEventType[]) {
    on(type, (event) => handleServerEvent(qc, event));
  }

  return () => es.close();
}

function handleServerEvent(queryClient: QueryClient, event: ServerEvent): void {
  if (event.type === 'sync.progress') {
    useSyncStore.getState().setProgress(event.accountId, {
      phase: event.phase,
      done: event.done,
      total: event.total,
    });
  }
  if (event.type === 'op.failed') {
    toast.error(`A mail action could not sync: ${event.error}`, {
      duration: 12_000,
    });
  }
  if (event.type === 'outbox.updated' && event.error) {
    toast.warning(
      `Message sent, but its Sent copy is still retrying: ${event.error}`,
      { duration: 12_000 },
    );
  }
  void invalidateServerEvent(queryClient, event);
}
